import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { canCreateProject } from 'utils/rbacUtils';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    IconButton,
    LinearProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    Tabs,
    Tab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    Code as CodeIcon,
    AccountTree as WorkflowIcon,
    Storage as StorageIcon,
    Storage as DatabaseIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    Folder as FolderIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Archive as ArchiveIcon
} from '@mui/icons-material';

// Import mock data
import { projectsData, userManagementData } from '../../dummy_data/datahubMockData';

const ProjectManagement = () => {
    const account = useSelector((state) => state.account);
    const canCreate = canCreateProject(account?.user);
    const [selectedTab, setSelectedTab] = useState(0);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openProjectDialog, setOpenProjectDialog] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [newProject, setNewProject] = useState({
        name: '',
        description: '',
        tags: [],
        members: []
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'archived':
                return 'default';
            case 'stopped':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <CheckCircleIcon color="success" />;
            case 'archived':
                return <ArchiveIcon color="default" />;
            case 'stopped':
                return <WarningIcon color="warning" />;
            default:
                return <WarningIcon color="default" />;
        }
    };

    const handleCreateProject = () => {
        // Mock project creation
        console.log('Creating project:', newProject);
        setOpenCreateDialog(false);
        setNewProject({ name: '', description: '', tags: [], members: [] });
    };

    const handleOpenProject = (project) => {
        setSelectedProject(project);
        setOpenProjectDialog(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const TabPanel = ({ children, value, index }) => (
        <div hidden={value !== index} style={{ paddingTop: '20px' }}>
            {value === index && children}
        </div>
    );

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" component="h1">
                        Project Management
                    </Typography>
                    {canCreate && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)}>
                            Create New Project
                        </Button>
                    )}
                </Box>
                <Typography variant="body1" color="text.secondary">
                    Manage your data projects, teams, and resources
                </Typography>
            </Box>

            {/* Project Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'success.main' }}>
                                    <FolderIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{projectsData.totalProjects}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Projects
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    <CheckCircleIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{projectsData.activeProjects}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active Projects
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'warning.main' }}>
                                    <ArchiveIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{projectsData.archivedProjects}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Archived Projects
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'info.main' }}>
                                    <PeopleIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{userManagementData.users.length}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Team Members
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Projects List */}
            <Card>
                <CardContent>
                    <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                        <Tab label="All Projects" />
                        <Tab label="Active" />
                        <Tab label="Archived" />
                    </Tabs>

                    <TabPanel value={selectedTab} index={0}>
                        <List>
                            {projectsData.projects.map((project, index) => (
                                <React.Fragment key={project.id}>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: getStatusColor(project.status) + '.main' }}>
                                                <FolderIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1">{project.name}</Typography>
                                                    <Chip label={project.status} size="small" color={getStatusColor(project.status)} />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {project.description}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                                        <Typography variant="caption">Created: {formatDate(project.createdAt)}</Typography>
                                                        <Typography variant="caption">
                                                            Storage: {project.storage.used} / {project.storage.limit}
                                                        </Typography>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={project.storage.percentage}
                                                            sx={{ width: 100, height: 6 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => handleOpenProject(project)}>
                                                <SettingsIcon />
                                            </IconButton>
                                            <IconButton size="small" color="secondary">
                                                <CodeIcon />
                                            </IconButton>
                                            <IconButton size="small" color="info">
                                                <WorkflowIcon />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                    {index < projectsData.projects.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={1}>
                        <List>
                            {projectsData.projects
                                .filter((p) => p.status === 'active')
                                .map((project, index) => (
                                    <React.Fragment key={project.id}>
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: 'success.main' }}>
                                                    <FolderIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={project.name} secondary={project.description} />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton size="small" color="primary">
                                                    <PlayIcon />
                                                </IconButton>
                                                <IconButton size="small" color="secondary">
                                                    <CodeIcon />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        {index < projectsData.projects.filter((p) => p.status === 'active').length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                        </List>
                    </TabPanel>

                    <TabPanel value={selectedTab} index={2}>
                        <List>
                            {projectsData.projects
                                .filter((p) => p.status === 'archived')
                                .map((project, index) => (
                                    <React.Fragment key={project.id}>
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: 'default.main' }}>
                                                    <ArchiveIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={project.name} secondary={project.description} />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton size="small" color="primary">
                                                    <PlayIcon />
                                                </IconButton>
                                                <IconButton size="small" color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        {index < projectsData.projects.filter((p) => p.status === 'archived').length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                        </List>
                    </TabPanel>
                </CardContent>
            </Card>

            {/* Create Project Dialog */}
            <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Project Name"
                                value={newProject.name}
                                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                value={newProject.description}
                                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Tags (comma separated)"
                                placeholder="analytics, ml, customer"
                                value={newProject.tags.join(', ')}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        tags: e.target.value.split(',').map((tag) => tag.trim())
                                    })
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Team Members</InputLabel>
                                <Select
                                    multiple
                                    value={newProject.members}
                                    onChange={(e) => setNewProject({ ...newProject, members: e.target.value })}
                                    input={<OutlinedInput label="Team Members" />}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {userManagementData.users.map((user) => (
                                        <MenuItem key={user.id} value={user.email}>
                                            <Checkbox checked={newProject.members.indexOf(user.email) > -1} />
                                            {user.name} ({user.email})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateProject} variant="contained">
                        Create Project
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Project Details Dialog */}
            <Dialog open={openProjectDialog} onClose={() => setOpenProjectDialog(false)} maxWidth="lg" fullWidth>
                {selectedProject && (
                    <>
                        <DialogTitle>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: getStatusColor(selectedProject.status) + '.main' }}>
                                    <FolderIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6">{selectedProject.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Created {formatDate(selectedProject.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <Typography variant="h6" gutterBottom>
                                        Project Overview
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        {selectedProject.description}
                                    </Typography>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Tags
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {selectedProject.tags.map((tag, index) => (
                                                <Chip key={index} label={tag} size="small" />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Storage Usage
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="body2">
                                                {selectedProject.storage.used} / {selectedProject.storage.limit}
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={selectedProject.storage.percentage}
                                                sx={{ flexGrow: 1, height: 8 }}
                                            />
                                            <Typography variant="body2">{selectedProject.storage.percentage}%</Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="h6" gutterBottom>
                                        Services Status
                                    </Typography>
                                    <List dense>
                                        {Object.entries(selectedProject.services).map(([service, info]) => (
                                            <ListItem key={service}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ width: 32, height: 32 }}>
                                                        {service === 'jupyterhub' && <CodeIcon />}
                                                        {service === 'airflow' && <WorkflowIcon />}
                                                        {service === 'mongodb' && <DatabaseIcon />}
                                                        {service === 'spark' && <StorageIcon />}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={service.charAt(0).toUpperCase() + service.slice(1)}
                                                    secondary={
                                                        <Chip label={info.status} size="small" color={getStatusColor(info.status)} />
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="h6" gutterBottom>
                                        Team Members
                                    </Typography>
                                    <List dense>
                                        {selectedProject.members.map((member) => (
                                            <ListItem key={member.id}>
                                                <ListItemAvatar>
                                                    <Avatar sx={{ width: 32, height: 32 }}>
                                                        <PeopleIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={member.id} secondary={member.role} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenProjectDialog(false)}>Close</Button>
                            <Button variant="contained" startIcon={<EditIcon />}>
                                Edit Project
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default ProjectManagement;
